import { styled, keyframes } from "@mui/material/styles";
import theme from "../theme";

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

interface CodeTextProps {
  top: string;
  left?: string;
  right?: string;
}

interface RectangleProps {
  top: number;
  left: number;
  width: number;
  height: number;
  color: string;
}

export const CodeTextStyle = styled("div")<CodeTextProps>(
  ({ top, left, right, theme }) => ({
    position: "absolute",
    top,
    right: "0%",
    left,
    padding: "30px 20px 15px 20px",
    minHeight: "330px",
    color: "#d4d4d4",
    fontSize: "1.3rem",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "5px 10px 15px 0px rgb(209 190 146 / 61%)",
    fontFamily: "'Fira Code', monospace",
    whiteSpace: "pre",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    cursor: "default",
    "@media (min-width: 2560px)": {
      fontSize: "2rem",
      minHeight: "500px",
    },
    [theme.breakpoints.down("xl")]: {
      fontSize: "0.5rem",
      minHeight: "130px",
      top: "0%",
    },
    [theme.breakpoints.between("lg", "xl")]: {
      fontSize: "0.6rem",
      minHeight: "160px",
      top: "0%",
      left: "70%",
      right: "0%",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "0.5rem",
      minHeight: "130px",
      top: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.5rem",
      minHeight: "130px",
      top: "125%",
      left: "auto",
      right: "12%",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.5rem",
      minHeight: "130px",
      top: "none",
    },
  })
);

export const TypingText = styled("div")({
  whiteSpace: "nowrap",
  fontFamily: "'Fira Code', monospace",
  fontSize: "1.25rem",
  [theme.breakpoints.down("md")]: {
    fontSize: "1rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
  },
});

export const TypewriterCursor = styled("span")({
  animation: `${blink} 0.75s step-end infinite`,
});

export const BrowserControls = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "8px",
  left: "8px",
  display: "flex",
  gap: "5px",
  "& div": {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
  },
  "& div:nth-of-type(1)": {
    backgroundColor: `${theme.palette.custom.darkgrey}`,
  },
  "& div:nth-of-type(2)": {
    backgroundColor: `${theme.palette.custom.hellbraun1}`,
  },
  "& div:nth-of-type(3)": {
    backgroundColor: `${theme.palette.custom.hellbraun3}`,
  },
  [theme.breakpoints.down("md")]: {
    top: "6px",
    left: "6px",
    "& div": {
      width: "10px",
      height: "10px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    top: "4px",
    left: "4px",
    "& div": {
      width: "8px",
      height: "8px",
    },
  },
}));

export const HeroContainer = styled("div")({
  width: "100%",
  height: "90vh",
  overflow: "visible",
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  [theme.breakpoints.down("xl")]: {
    height: "85vh",
  },
  [theme.breakpoints.down("lg")]: {
    height: "80vh",
    overflow: "hidden",
  },
  [theme.breakpoints.down("md")]: {
    height: "75vh",
    overflow: "hidden",
  },
  [theme.breakpoints.down("sm")]: {
    height: "100vh",
    overflow: "hidden",
  },
});

export const Rectangle = styled("div")<RectangleProps>(
  ({ top, left, width, height, color }) => ({
    position: "absolute",
    backgroundColor: color,
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: "4px",
    boxShadow: "2px 2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: -999,

    [theme.breakpoints.down("xl")]: {
      width: `${width * 0.9}px`,
      height: `${height * 0.9}px`,
    },
    [theme.breakpoints.down("lg")]: {
      width: `${width * 0.8}px`,
      height: `${height * 0.8}px`,
    },
    [theme.breakpoints.down("md")]: {
      width: `${width * 0.7}px`,
      height: `${height * 0.7}px`,
    },
    [theme.breakpoints.down("sm")]: {
      width: `${width * 0.6}px`,
      height: `${height * 0.6}px`,
    },
    [theme.breakpoints.down("xs")]: {
      width: `${width * 0.5}px`,
      height: `${height * 0.5}px`,
    },
  })
);

export const FallbackContainer = styled("div")({
  width: "100%",
  height: "100%",
  position: "relative",
});

export const OverlayContent = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "10%",
  transform: "translateY(-50%)",
  color: "#ffffff",
  fontSize: "2rem",
  textAlign: "left",
  padding: "20px",
  borderRadius: "10px",
  width: "80%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  [theme.breakpoints.down("md")]: {
    left: "5%",
    fontSize: "1.5rem",
    padding: "15px",
    width: "90%",
    top: "30%",
  },
  [theme.breakpoints.down("sm")]: {
    left: "2%",
    fontSize: "1.2rem",
    padding: "10px",
    width: "95%",
  },
}));

export const LogoImage = styled("img")({
  width: "550px",
  [theme.breakpoints.down("md")]: {
    width: "400px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "300px",
  },
});
